import { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Mail,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  X,
  Info,
  Loader2,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Roles } from "@/enums/roles.enum";
import { useAuth } from "@/context/AuthContext";
import { userApi, BulkUserValidationResult } from "@/services/userService";
import { projectApi } from "@/services/projectService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { GetProjectsResponse } from "@/services/projectService";
import { GetUserResponseDto } from "@/dto/get-user-response.dto";

interface BulkInviteUser {
  email: string;
  status:
    | "pending"
    | "checking"
    | "exists"
    | "new"
    | "error"
    | "inviting"
    | "success"
    | "failed";
  user?: GetUserResponseDto;
  projectMemberships?: string[]; // Project IDs where user is already a member
  pendingInvitations?: string[]; // Project IDs where user has pending invitations
  error?: string;
  retryCount: number;
}

interface BulkInviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_CONCURRENT_REQUESTS = 5;
const MAX_RETRY_ATTEMPTS = 3;

export function BulkInviteModal({ open, onOpenChange }: BulkInviteModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Step management
  const [currentStep, setCurrentStep] = useState<
    "input" | "review" | "processing"
  >("input");

  // Form data
  const [emailInput, setEmailInput] = useState("");
  const [selectedRole, setSelectedRole] = useState<Roles>(Roles.CLIENT);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [users, setUsers] = useState<BulkInviteUser[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedInvites, setCompletedInvites] = useState(0);

  // Get available roles based on current user
  const availableRoles = useMemo(() => {
    if (!user) return [];
    if (user.role === Roles.ADMIN) {
      return [Roles.ADMIN, Roles.VENDOR, Roles.CLIENT, Roles.CONTRACTOR, Roles.MEMBER];
    }
    if (user.role === Roles.VENDOR) {
      return [Roles.CLIENT, Roles.CONTRACTOR];
    }
    return [];
  }, [user]);

  // Get projects for selection
  const { data: projectsData } = useQuery<GetProjectsResponse>({
    queryKey: ["projects"],
    queryFn: () => projectApi.getProjects(),
    enabled: open,
  });

  // Parse emails from input
  const parseEmails = useCallback((input: string): string[] => {
    return input
      .split(/[,\n\r\t;]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
      .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  }, []);

  // User checking mutation
  const bulkCheckUsersMutation = useMutation({
    mutationFn: async (data: { emails: string[]; projectIds?: string[] }) => {
      return userApi.bulkValidateUsers(data);
    },
  });

  // Invitation mutations
  const bulkInviteMutation = useMutation({
    mutationFn: (data: { emails: string[]; role: string; projectIds?: string[] }) =>
      userApi.bulkInviteUsers(data),
  });

  // Process Step 1: Validate emails and check users
  const handleNext = async () => {
    if (currentStep === "input") {
      const emails = parseEmails(emailInput);

      if (emails.length === 0) {
        toast({
          title: "No valid emails",
          description: "Please enter at least one valid email address",
          variant: "destructive",
        });
        return;
      }

      if (emails.length > 100) {
        toast({
          title: "Too many emails",
          description: "Please limit to 100 emails at a time",
          variant: "destructive",
        });
        return;
      }

      // Initialize user objects
      const initialUsers: BulkInviteUser[] = emails.map((email) => ({
        email,
        status: "checking",
        retryCount: 0,
      }));

      setUsers(initialUsers);
      setCurrentStep("review");

      try {
        // Use bulk validation with project IDs if available
        const result = await bulkCheckUsersMutation.mutateAsync({
          emails,
          projectIds: projectsData?.projects?.map(p => p._id) || [],
        });

        // Update users with bulk validation results
        setUsers(result.results.map((validationResult: BulkUserValidationResult) => ({
          email: validationResult.email,
          status: validationResult.error 
            ? ("error" as const)
            : validationResult.exists 
              ? ("exists" as const) 
              : ("new" as const),
          user: validationResult.user ? {
            ...validationResult.user,
            role: validationResult.user.role as Roles,
            superAdmin: false, // Default value since it's not returned by bulk validation
            createdAt: new Date(), // Default value
            updatedAt: new Date(), // Default value
          } : undefined,
          projectMemberships: validationResult.projectMemberships,
          pendingInvitations: validationResult.pendingInvitations,
          error: validationResult.error,
          retryCount: 0,
        })));

      } catch (error) {
        console.error("Bulk validation failed:", error);
        toast({
          title: "Validation failed",
          description: "Failed to validate users. Please try again.",
          variant: "destructive",
        });
        
        // Update all users to error status
        setUsers(initialUsers.map(user => ({
          ...user,
          status: "error" as const,
          error: "Failed to validate user",
        })));
      }
    }
  };

  // Calculate actual invitations to be sent (excluding existing members and pending invitations)
  const calculateActualInvitations = useMemo(() => {
    const validUsers = users.filter(
      (u) => u.status === "exists" || u.status === "new" || u.status === "failed"
    );
    
    // Since we're using bulk invite, we count unique users that can receive invitations
    return validUsers.filter(user => {
      // Check if user can be invited to at least one selected project
      return selectedProjects.some(projectId => 
        !user.projectMemberships?.includes(projectId) &&
        !user.pendingInvitations?.includes(projectId)
      );
    }).length;
  }, [users, selectedProjects]);

  // Process Step 3: Send invitations
  const handleSendInvites = async () => {
    if (selectedProjects.length === 0) {
      toast({
        title: "No projects selected",
        description: "Please select at least one project",
        variant: "destructive",
      });
      return;
    }

    setCurrentStep("processing");
    setIsProcessing(true);
    setCompletedInvites(0);

    const validUsers = users.filter(
      (u) =>
        u.status === "exists" || u.status === "new" || u.status === "failed"
    );

    try {
      // Use bulk invite API
      const emails = validUsers.map(u => u.email);
      
      const bulkInviteResult = await bulkInviteMutation.mutateAsync({
        emails,
        role: selectedRole,
        projectIds: selectedProjects,
      });

      // Update user statuses based on bulk invite results
      setUsers((prev) =>
        prev.map((user) => {
          const result = bulkInviteResult.results.find(r => r.email === user.email);
          if (result) {
            return {
              ...user,
              status: result.success ? ("success" as const) : ("failed" as const),
              error: result.success ? undefined : result.error,
            };
          }
          return user;
        })
      );

      setCompletedInvites(validUsers.length);

      if (bulkInviteResult.successCount > 0) {
        toast({
          title: "Invitations sent",
          description: `Successfully sent ${bulkInviteResult.successCount} invitation${bulkInviteResult.successCount > 1 ? 's' : ''}`,
        });
      }

      if (bulkInviteResult.failureCount > 0) {
        toast({
          title: "Some invitations failed",
          description: `${bulkInviteResult.failureCount} invitation${bulkInviteResult.failureCount > 1 ? 's' : ''} failed to send`,
          variant: "destructive",
        });
      }

    } catch (error: unknown) {
      let errorMessage = "Failed to send invitations";
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Invitation failed",
        description: errorMessage,
        variant: "destructive",
      });

      // Mark all users as failed
      setUsers((prev) =>
        prev.map((user) => ({
          ...user,
          status: "failed" as const,
          error: errorMessage,
        }))
      );
    }

    setIsProcessing(false);
  };

  // Retry failed invitation (for individual users)
  const retryInvitation = async (userEmail: string) => {
    const user = users.find((u) => u.email === userEmail);
    if (!user || user.retryCount >= MAX_RETRY_ATTEMPTS) return;

    try {
      // Retry with bulk invite for single user
      const bulkInviteResult = await bulkInviteMutation.mutateAsync({
        emails: [userEmail],
        role: selectedRole,
        projectIds: selectedProjects,
      });

      const result = bulkInviteResult.results[0];
      if (result) {
        setUsers((prev) =>
          prev.map((u) =>
            u.email === userEmail
              ? {
                  ...u,
                  status: result.success ? ("success" as const) : ("failed" as const),
                  error: result.success ? undefined : result.error,
                  retryCount: u.retryCount + 1,
                }
              : u
          )
        );

        if (result.success) {
          toast({
            title: "Retry successful",
            description: `Successfully invited ${userEmail}`,
          });
        } else {
          toast({
            title: "Retry failed",
            description: result.error || "Failed to invite user",
            variant: "destructive",
          });
        }
      }
    } catch (error: unknown) {
      let errorMessage = "Retry failed";
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.email === userEmail
            ? {
                ...u,
                status: "failed" as const,
                error: errorMessage,
                retryCount: u.retryCount + 1,
              }
            : u
        )
      );

      toast({
        title: "Retry failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Reset modal state
  const handleClose = () => {
    setCurrentStep("input");
    setEmailInput("");
    setUsers([]);
    setSelectedProjects([]);
    setCompletedInvites(0);
    setIsProcessing(false);
    queryClient.invalidateQueries({ queryKey: ["users"] });
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    onOpenChange(false);
  };

  // Get status badge variant and icon
  const getStatusDisplay = (status: BulkInviteUser["status"]) => {
    switch (status) {
      case "pending":
        return { icon: Users, variant: "secondary" as const, text: "Pending" };
      case "checking":
        return {
          icon: Loader2,
          variant: "secondary" as const,
          text: "Checking...",
        };
      case "exists":
        return {
          icon: CheckCircle2,
          variant: "default" as const,
          text: "Existing User",
        };
      case "new":
        return { icon: Mail, variant: "outline" as const, text: "New User" };
      case "inviting":
        return {
          icon: Loader2,
          variant: "secondary" as const,
          text: "Inviting...",
        };
      case "success":
        return {
          icon: CheckCircle2,
          variant: "default" as const,
          text: "Invited",
        };
      case "failed":
        return {
          icon: AlertCircle,
          variant: "destructive" as const,
          text: "Failed",
        };
      case "error":
        return {
          icon: AlertCircle,
          variant: "destructive" as const,
          text: "Error",
        };
    }
  };

  // Don't render if user doesn't have permission
  if (availableRoles.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Bulk Invite Users
          </DialogTitle>
          <DialogDescription>
            Invite multiple users to projects at once. Users can be existing or
            new.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Email Input */}
        {currentStep === "input" && (
          <div className="flex-1 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emails">Email Addresses</Label>
              <Textarea
                id="emails"
                placeholder="Enter email addresses separated by commas or new lines&#10;example@company.com, another@company.com&#10;third@company.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="min-h-[120px] resize-none"
              />
              <p className="text-sm text-muted-foreground">
                Separate multiple emails with commas, semicolons, or new lines.
                Maximum 100 emails.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={selectedRole}
                  onValueChange={(value: Roles) => setSelectedRole(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Projects</Label>
                <Select
                  value={selectedProjects.join(",")}
                  onValueChange={(value) => {
                    if (value) {
                      setSelectedProjects(value.split(","));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select projects">
                      {selectedProjects.length > 0
                        ? `${selectedProjects.length} project${
                            selectedProjects.length > 1 ? "s" : ""
                          } selected`
                        : "Select projects"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {projectsData?.projects?.map((project) => (
                      <SelectItem key={project._id} value={project._id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Review Users */}
        {currentStep === "review" && (
          <div className="flex-1 space-y-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Review Users ({users.length})</h3>
                <p className="text-sm text-muted-foreground">
                  Role: <Badge variant="outline">{selectedRole}</Badge>
                  {selectedProjects.length > 0 && (
                    <> • Projects: {selectedProjects.length} selected</>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                {users.filter((u) => u.status === "exists").length} existing
                <Mail className="h-4 w-4 text-blue-600" />
                {users.filter((u) => u.status === "new").length} new
                <AlertCircle className="h-4 w-4 text-red-600" />
                {users.filter((u) => u.status === "error").length} errors
              </div>
            </div>

            <div className="flex-1 overflow-auto border rounded-md">
              <div className="space-y-1 p-2">
                {users.map((user) => {
                  const statusDisplay = getStatusDisplay(user.status);
                  const StatusIcon = statusDisplay.icon;

                  return (
                    <Card key={user.email} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <StatusIcon
                            className={`h-4 w-4 ${
                              user.status === "checking" ||
                              user.status === "inviting"
                                ? "animate-spin"
                                : ""
                            }`}
                          />
                          <div>
                            <p className="font-medium">{user.email}</p>
                            {user.user && (
                              <p className="text-sm text-muted-foreground">
                                {user.user.firstName} {user.user.lastName} •{" "}
                                {user.user.role}
                              </p>
                            )}
                            {/* Show project membership info */}
                            {(user.projectMemberships && user.projectMemberships.length > 0) && (
                              <p className="text-xs text-blue-600">
                                Member of {user.projectMemberships.length} selected project(s)
                              </p>
                            )}
                            {(user.pendingInvitations && user.pendingInvitations.length > 0) && (
                              <p className="text-xs text-orange-600">
                                Pending invites to {user.pendingInvitations.length} selected project(s)
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge variant={statusDisplay.variant}>
                            {statusDisplay.text}
                          </Badge>

                          {user.error && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Info className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80">
                                <div className="space-y-2">
                                  <h4 className="font-medium text-destructive">
                                    Error Details
                                  </h4>
                                  <p className="text-sm">{user.error}</p>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Project Selection */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Select Projects to Invite Users To:</Label>
                {selectedProjects.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {calculateActualInvitations} invitations will be sent
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-auto">
                {projectsData?.projects?.map((project) => (
                  <label
                    key={project._id}
                    className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProjects((prev) => [...prev, project._id]);
                        } else {
                          setSelectedProjects((prev) =>
                            prev.filter((id) => id !== project._id)
                          );
                        }
                      }}
                    />
                    <span className="text-sm">{project.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {currentStep === "processing" && (
          <div className="flex-1 space-y-4 overflow-hidden flex flex-col">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Sending Invitations</h3>
                <div className="text-sm text-muted-foreground">
                  {completedInvites} /{" "}
                  {users.filter((u) => u.status !== "error").length}{" "}
                  completed
                </div>
              </div>

              {isProcessing && (
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        (completedInvites /
                          users.filter((u) => u.status !== "error").length) *
                        100
                      }%`,
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto border rounded-md">
              <div className="space-y-1 p-2">
                {users.map((user) => {
                  const statusDisplay = getStatusDisplay(user.status);
                  const StatusIcon = statusDisplay.icon;

                  return (
                    <Card key={user.email} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <StatusIcon
                            className={`h-4 w-4 ${
                              user.status === "inviting" ? "animate-spin" : ""
                            }`}
                          />
                          <div>
                            <p className="font-medium">{user.email}</p>
                            <p className="text-sm text-muted-foreground">
                              {selectedProjects.length} project
                              {selectedProjects.length > 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge variant={statusDisplay.variant}>
                            {statusDisplay.text}
                          </Badge>

                          {user.status === "failed" &&
                            user.retryCount < MAX_RETRY_ATTEMPTS && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => retryInvitation(user.email)}
                                disabled={isProcessing}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}

                          {user.error && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Info className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80">
                                <div className="space-y-2">
                                  <h4 className="font-medium text-destructive">
                                    Error Details
                                  </h4>
                                  <p className="text-sm">{user.error}</p>
                                  {user.retryCount < MAX_RETRY_ATTEMPTS && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        retryInvitation(user.email)
                                      }
                                      disabled={isProcessing}
                                    >
                                      Retry (
                                      {MAX_RETRY_ATTEMPTS - user.retryCount}{" "}
                                      attempts left)
                                    </Button>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Step indicators */}
            <div className="flex items-center gap-1">
              <div
                className={`w-2 h-2 rounded-full ${
                  currentStep === "input" ? "bg-primary" : "bg-muted"
                }`}
              />
              <div
                className={`w-2 h-2 rounded-full ${
                  currentStep === "review" ? "bg-primary" : "bg-muted"
                }`}
              />
              <div
                className={`w-2 h-2 rounded-full ${
                  currentStep === "processing" ? "bg-primary" : "bg-muted"
                }`}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {currentStep === "review" && (
              <Button variant="outline" onClick={() => setCurrentStep("input")}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}

            {currentStep === "input" && (
              <Button onClick={handleNext} disabled={!emailInput.trim()}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {currentStep === "review" && (
              <Button
                onClick={handleSendInvites}
                disabled={
                  selectedProjects.length === 0 ||
                  calculateActualInvitations === 0
                }
              >
                Send Invitations to{" "}
                {calculateActualInvitations}{" "}
                User{calculateActualInvitations !== 1 ? "s" : ""}
              </Button>
            )}

            {currentStep === "processing" && (
              <Button onClick={handleClose} disabled={isProcessing}>
                {isProcessing ? "Processing..." : "Close"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
